package com.evoapps.evoshift.domain.usecase.courses

import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.domain.model.Course
import com.evoapps.evoshift.domain.model.CourseAssignment
import com.evoapps.evoshift.domain.model.CourseAssignments
import com.evoapps.evoshift.domain.model.CourseFiles
import com.evoapps.evoshift.domain.model.CourseGrading
import com.evoapps.evoshift.domain.model.CourseKey
import com.evoapps.evoshift.domain.model.CourseProblem
import com.evoapps.evoshift.domain.model.CourseProgress
import com.evoapps.evoshift.domain.model.CourseProgressReport
import com.evoapps.evoshift.domain.model.CourseProgressRow
import com.evoapps.evoshift.domain.model.CourseResult
import com.evoapps.evoshift.domain.model.CourseRules
import com.evoapps.evoshift.domain.model.CourseStatus
import com.evoapps.evoshift.domain.model.DocumentFiles
import com.evoapps.evoshift.domain.model.InvalidCourseException
import com.evoapps.evoshift.domain.model.InvalidDocumentException
import com.evoapps.evoshift.domain.model.MyCourse
import com.evoapps.evoshift.domain.model.OhsConfirmation
import com.evoapps.evoshift.domain.model.OhsEntry
import com.evoapps.evoshift.domain.model.OhsRecord
import com.evoapps.evoshift.domain.model.PresentationFiles
import com.evoapps.evoshift.domain.model.TrainingStatus
import com.evoapps.evoshift.domain.model.User
import com.evoapps.evoshift.domain.repository.CourseRepository
import com.evoapps.evoshift.domain.repository.OhsDocumentKind
import com.evoapps.evoshift.domain.repository.OhsDocumentLink
import com.evoapps.evoshift.domain.repository.UserRepository
import kotlinx.datetime.LocalDate
import kotlin.time.Instant

/*
 * SH-126: the use cases behind courses and the OHS training record. Who may call what is the
 * screens' (Gate) and the rules' (firestore.rules, functions/courses.js).
 */

/** The workplace's courses for its managers (drafts too), newest change first. */
class GetManagedCoursesUseCase(private val courses: CourseRepository) {
    suspend operator fun invoke(workplaceId: String): Result<List<Course>> =
        courses.getCourses(workplaceId, includeDrafts = true).map { list -> list.sortedByDescending { it.updatedAt } }
}

/** A course with its answer key, for the editor (managers). */
class GetCourseForEditingUseCase(private val courses: CourseRepository) {
    suspend operator fun invoke(workplaceId: String, courseId: String): Result<Pair<Course, CourseKey>> = runCatching {
        val course = courses.getCourse(workplaceId, courseId).getOrThrow()
        val key = courses.getKey(workplaceId, courseId).getOrThrow() ?: CourseKey(courseId, course.version, emptyMap())
        course to key
    }
}

/**
 * Saves a course as a draft, or publishes it ([publish]; changing a published one always publishes): a published course must pass every
 * check ([CourseRules.problems] with forPublish), its version goes up (results keep the version
 * they were for) and the key gets the same version. A draft is only checked for its limits.
 * OHS courses always count for readiness.
 */
class SaveCourseUseCase(
    private val courses: CourseRepository,
    private val now: () -> Instant = { AppClock.now() }
) {
    suspend operator fun invoke(course: Course, correct: Map<String, List<Int>>, publish: Boolean, editorId: String): Result<Course> = runCatching {
        val cleaned = course.copy(
            title = course.title.trim(),
            description = course.description.trim(),
            roles = course.roles.map { it.trim() }.filter { it.isNotEmpty() }.distinct().take(CourseRules.MAX_ROLES),
            countsForReadiness = course.countsForReadiness || course.kind.isOhs,
            ohs = if (course.kind.isOhs) course.ohs else null,
            // SH-211: the lesson sanitised, with its plain-text version for older app versions.
            pages = course.pages.map { page -> CourseRules.forStorage(page.copy(url = page.url?.trim()?.takeIf { it.isNotEmpty() })) }
        )
        val key = correct.filterKeys { id -> cleaned.questions.any { it.id == id } }
        // A published course changes only by publishing again (a new version for new results).
        val publishing = publish || cleaned.isPublished
        val problems = CourseRules.problems(cleaned, key, forPublish = publishing)
            .let { if (publishing) it else it.filterTo(mutableSetOf()) { p -> p in DRAFT_CHECKS } }
        if (problems.isNotEmpty()) throw InvalidCourseException(problems)
        val time = now()
        val stored = if (publishing) {
            cleaned.copy(
                status = CourseStatus.PUBLISHED,
                version = cleaned.version + 1,
                publishedAt = cleaned.publishedAt ?: time,
                updatedAt = time,
                updatedBy = editorId
            )
        } else {
            cleaned.copy(updatedAt = time, updatedBy = editorId)
        }
        courses.saveCourse(stored, CourseKey(stored.id, stored.version, key)).getOrThrow()
    }

    private companion object {
        /** What even a draft must respect (the rules refuse more). */
        val DRAFT_CHECKS = setOf(
            CourseProblem.MISSING_TITLE,
            CourseProblem.TITLE_TOO_LONG,
            CourseProblem.TOO_MANY_PAGES,
            CourseProblem.PAGE_TOO_LONG,
            CourseProblem.COURSE_TOO_LARGE,
            CourseProblem.TOO_MANY_QUESTIONS,
            CourseProblem.BAD_PASS_PERCENT,
            CourseProblem.BAD_DUE_DAYS
        )
    }
}

class DeleteCourseUseCase(private val courses: CourseRepository) {
    suspend operator fun invoke(workplaceId: String, courseId: String): Result<Unit> = courses.deleteCourse(workplaceId, courseId)
}

/** A picked picture for a page: checked, uploaded; returns its Storage path. */
class UploadCourseImageUseCase(private val courses: CourseRepository) {
    suspend operator fun invoke(workplaceId: String, courseId: String, bytes: ByteArray): Result<String> = runCatching {
        CourseFiles.imageProblem(bytes)?.let { throw InvalidDocumentException(it) }
        courses.uploadImage(workplaceId, courseId, bytes, DocumentFiles.sniff(bytes)!!).getOrThrow()
    }
}

/**
 * SH-211: a presentation file picked for a page: a .pptx, .odp or PDF of at most
 * [PresentationFiles.MAX_BYTES] ([pdfOnly]: the PDF export next to one), read from its bytes,
 * uploaded to the workplace's Storage folder. Returns the path, the cleaned file name and its type.
 */
class UploadCoursePresentationUseCase(private val courses: CourseRepository) {
    data class Uploaded(val path: String, val fileName: String, val contentType: String)

    suspend operator fun invoke(workplaceId: String, courseId: String, fileName: String?, bytes: ByteArray, pdfOnly: Boolean = false): Result<Uploaded> = runCatching {
        PresentationFiles.problem(bytes, pdfOnly)?.let { throw InvalidDocumentException(it) }
        val type = DocumentFiles.sniff(bytes)!!
        val path = courses.uploadPresentation(workplaceId, courseId, bytes, type).getOrThrow()
        Uploaded(path, DocumentFiles.cleanFileName(fileName, type), type)
    }
}

/** SH-211: an uploaded presentation's bytes, with the reader's sign-in (storage.rules). */
class LoadCoursePresentationUseCase(private val courses: CourseRepository) {
    suspend operator fun invoke(path: String): Result<ByteArray> = courses.downloadPresentation(path)
}

/** An id for a course that is not saved yet (its pictures are stored under it). */
class NewCourseIdUseCase(private val courses: CourseRepository) {
    operator fun invoke(workplaceId: String): String = courses.newCourseId(workplaceId)
}

class LoadCourseImageUseCase(private val courses: CourseRepository) {
    suspend operator fun invoke(path: String): Result<ByteArray> = courses.downloadImage(path)
}

/**
 * The courses [user] should take at [workplaceId] with their progress and result: not passed
 * first (the earliest due day first), passed after.
 */
class GetMyCoursesUseCase(private val courses: CourseRepository) {
    suspend operator fun invoke(workplaceId: String, user: User): Result<List<MyCourse>> = runCatching {
        val published = courses.getCourses(workplaceId, includeDrafts = false).getOrThrow()
        val explicit = courses.getMyAssignments(workplaceId, user.uid, published.map { it.id }).getOrThrow().associateBy { it.courseId }
        CourseAssignments.forPerson(published, user.uid, user.engagements[workplaceId], explicit).map { assigned ->
            MyCourse(
                assigned = assigned,
                progress = courses.getProgress(workplaceId, assigned.course.id, user.uid).getOrNull(),
                completion = courses.getCompletion(workplaceId, assigned.course.id, user.uid).getOrNull()
            )
        }.sortedWith(compareBy<MyCourse> { it.passed }.thenBy { it.assigned.dueOn ?: LocalDate(9999, 12, 31) }.thenBy { it.course.title.lowercase() })
    }
}

/** One course for its viewer, with the reader's progress and result. */
class GetCourseToTakeUseCase(private val courses: CourseRepository) {
    suspend operator fun invoke(workplaceId: String, courseId: String, userId: String): Result<Triple<Course, CourseProgress?, com.evoapps.evoshift.domain.model.CourseCompletion?>> = runCatching {
        val course = courses.getCourse(workplaceId, courseId).getOrThrow()
        Triple(course, courses.getProgress(workplaceId, courseId, userId).getOrNull(), courses.getCompletion(workplaceId, courseId, userId).getOrNull())
    }
}

/** The person has seen [pageId]: adds it to their progress (kept once). */
class SaveCourseProgressUseCase(
    private val courses: CourseRepository,
    private val now: () -> Instant = { AppClock.now() }
) {
    suspend operator fun invoke(workplaceId: String, before: CourseProgress?, courseId: String, userId: String, pageId: String): Result<CourseProgress> = runCatching {
        if (before != null && pageId in before.pagesSeen) return@runCatching before
        val time = now()
        val progress = CourseProgress(
            courseId = courseId,
            userId = userId,
            pagesSeen = (before?.pagesSeen.orEmpty() + pageId).distinct().takeLast(CourseRules.MAX_PAGES),
            startedAt = before?.startedAt ?: time,
            updatedAt = time
        )
        courses.saveProgress(workplaceId, progress).getOrThrow()
        progress
    }
}

/** Every question needs an answer before the quiz goes to the server. */
class QuizNotFinishedException : IllegalStateException("Not every question is answered")

/** Sends the quiz for grading on the server (submitCourse). */
class SubmitCourseUseCase(private val courses: CourseRepository) {
    suspend operator fun invoke(course: Course, answers: Map<String, List<Int>>, confirm: Boolean): Result<CourseResult> {
        if (!CourseGrading.allAnswered(course.questions, answers)) return Result.failure(QuizNotFinishedException())
        return courses.submit(course.workplaceId, course.id, course.version, answers, confirm)
    }
}

/** Asks people personally to take a course by [dueOn]. */
class AssignCourseUseCase(
    private val courses: CourseRepository,
    private val now: () -> Instant = { AppClock.now() }
) {
    suspend operator fun invoke(workplaceId: String, courseId: String, userIds: Collection<String>, dueOn: LocalDate?, assignedBy: String): Result<Unit> = runCatching {
        val time = now()
        userIds.distinct().forEach { uid ->
            courses.assign(workplaceId, CourseAssignment(courseId, uid, dueOn, time, assignedBy)).getOrThrow()
        }
    }
}

class UnassignCourseUseCase(private val courses: CourseRepository) {
    suspend operator fun invoke(workplaceId: String, courseId: String, userId: String): Result<Unit> = courses.unassign(workplaceId, courseId, userId)
}

/**
 * Who a course is for and how far each of them got (managers): everyone it is assigned to
 * (personally, by position, as a new hire), plus results of people it is no longer for or who
 * left ([CourseProgressRow.former]). Not passed first.
 */
class GetCourseProgressReportUseCase(
    private val courses: CourseRepository,
    private val users: UserRepository
) {
    suspend operator fun invoke(course: Course): Result<CourseProgressReport> = runCatching {
        val wpId = course.workplaceId
        val team = users.getAssignableEmployees(wpId).getOrThrow()
        val explicit = courses.getAssignments(wpId, course.id).getOrThrow().associateBy { it.userId }
        val completions = courses.getCompletions(wpId, course.id).getOrThrow().associateBy { it.userId }
        val progress = courses.getAllProgress(wpId, course.id).getOrThrow().associateBy { it.userId }
        val rows = team.mapNotNull { person ->
            val mine = explicit[person.uid]?.let { mapOf(course.id to it) }.orEmpty()
            val assigned = CourseAssignments.forPerson(listOf(course), person.uid, person.engagements[wpId], mine).firstOrNull()
            if (assigned == null && person.uid !in completions) return@mapNotNull null
            CourseProgressRow(
                userId = person.uid,
                displayName = person.displayName,
                dueOn = assigned?.dueOn,
                pagesSeen = progress[person.uid]?.pagesSeen?.count { id -> course.pages.any { it.id == id } } ?: 0,
                completion = completions[person.uid]
            )
        }
        val inTeam = team.mapTo(HashSet()) { it.uid }
        val former = completions.values.filter { it.userId !in inTeam }.map { completion ->
            CourseProgressRow(completion.userId, completion.displayName.orEmpty(), null, 0, completion, former = true)
        }
        CourseProgressReport(
            course = course,
            rows = (rows.sortedWith(compareBy<CourseProgressRow> { it.passed }.thenBy { it.displayName.lowercase() }) + former)
        )
    }
}

class GetOhsRecordUseCase(private val courses: CourseRepository) {
    suspend operator fun invoke(workplaceId: String, userId: String): Result<OhsRecord?> = courses.getOhsRecord(workplaceId, userId)
}

/** A manager records a part of a person's OHS training; the server checks and stamps it. */
class RecordOhsTrainingUseCase(private val courses: CourseRepository) {
    suspend operator fun invoke(workplaceId: String, userId: String, entry: OhsEntry): Result<OhsRecord> = courses.recordOhs(workplaceId, userId, entry)
}

/** The person confirms a part of their OHS training (§ 12 ust. 1, electronic confirmation). */
class ConfirmOhsTrainingUseCase(private val courses: CourseRepository) {
    suspend operator fun invoke(workplaceId: String, part: OhsConfirmation): Result<OhsRecord> = courses.confirmOhs(workplaceId, part)
}

class GetOhsDocumentUseCase(private val courses: CourseRepository) {
    suspend operator fun invoke(workplaceId: String, userId: String, kind: OhsDocumentKind): Result<OhsDocumentLink> =
        courses.ohsDocument(workplaceId, userId, kind)
}

/** Everyone's training status at a workplace (managers), by person. */
class GetTrainingStatusesUseCase(private val courses: CourseRepository) {
    suspend operator fun invoke(workplaceId: String): Result<Map<String, TrainingStatus>> =
        courses.getTrainingStatuses(workplaceId).map { list -> list.associateBy { it.userId } }
}
